import { isNonEmptyArray } from '@shared/utils/array';
import type { MeasurementSystem } from '@/feature/userPreference/api.types';
import { attachToDebug } from '@/util/debug';
import { isSampleSubscription, Subscription as TSubscription } from './types';
import { createKey } from './util';

type EventType = 'change';

type EventHandler = (event: StreamChangeEvent) => void;

export type StreamChangeEvent = {
  measurementSystem: MeasurementSystem;
  subscriptions: TSubscription[];
};

// Never import the class directly, instead import the singleton instance.
// The class exported to make it testable.
export class StreamConfig {
  private subscriptions: Record<string, TSubscription> = {};
  private measurementSystem: MeasurementSystem = 'metric';
  private events: Record<string, EventHandler[]> = {};

  addSubscription(subscription: TSubscription) {
    if (!isValid(subscription)) {
      return () => {};
    }
    const key = createKey(subscription.type);
    this.subscriptions[key] = subscription;
    this.emit('change');

    return () => {
      delete this.subscriptions[key];
      this.emit('change');
    };
  }

  getSubscriptions() {
    return Object.values(this.subscriptions);
  }

  getMeasurementSystem() {
    return this.measurementSystem;
  }

  setMeasurementSystem(measurementSystem: MeasurementSystem) {
    if (this.measurementSystem === measurementSystem) {
      return;
    }
    this.measurementSystem = measurementSystem;
    this.emit('change');
  }

  getState(): StreamChangeEvent {
    return {
      measurementSystem: this.measurementSystem,
      subscriptions: Object.values(this.subscriptions),
    };
  }

  on(event: EventType, handler: EventHandler) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(handler);
  }

  off(event: EventType, handler: EventHandler) {
    if (this.events[event]) {
      const index = this.events[event].findIndex((item) => item === handler);
      this.events[event].splice(index, 1);
    }
  }

  emit(eventType: EventType) {
    const event = this.getState();
    this.events[eventType]?.forEach((handler) => handler(event));
  }
}

function isValid(subscription: TSubscription): boolean {
  if (isSampleSubscription(subscription)) {
    if (!isNonEmptyArray(subscription.equipmentIds)) {
      return false;
    }
    if (!isNonEmptyArray(subscription.signalNames)) {
      return false;
    }
  }

  return true;
}

export const streamConfig = new StreamConfig();
attachToDebug('dataStream', streamConfig);
